import {AfterViewInit, Component, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../../shared/utils.service";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {AssetTypeModel} from "../../models/asset-type.model";
import {AssetTypesEditComponent} from "../asset-types-edit/asset-types-edit.component";
import {AssetTypesViewComponent} from "../asset-types-view/asset-types-view.component";
import {AssetTypesFilterComponent} from "../asset-types-filter/asset-types-filter.component";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../../utils/api-data-source";
import {ActivatedRoute, Router} from "@angular/router";
import {saveAs} from "file-saver";
import moment from "moment";
import {RBACService} from "../../shared/rbac.service";

@Component({
  selector: 'app-asset-subtypes',
  templateUrl: './asset-subtypes.component.html',
  styleUrls: ['./asset-subtypes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetSubtypesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AssetSubtypesDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit(). Subtypes are part of the Asset Types
  // feature, so they share its permissions.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically (NOT as a static array).
  displayedColumns: string[] = [];

  on_parent_list: boolean = false;
  parent_id: number;

  constructor(
    private utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSET_TYPES, RBACService.RBAC_A.A_DELETE)
    ], true);

    // 2. Page-level access: reaching a subtype list means drilling into a specific parent type, so
    // the view permission is the base requirement here (unlike the top level list, which a
    // create-only user may open).
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset types.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'id',
      'name',
      'description',
      'linked_assets_count',
      'created_by_user',
      'date_created',
      'buttons'
    );

    // Extract the parent id from the route params.
    this.parent_id = Number(this.route.parent?.snapshot.params['parent_id']);

    // Redirect the user back to the list of Asset Types if the parent id is missing.
    if ( !this.parent_id ) {
      this.router.navigate(['/asset-types']);
      return;
    }

    // Store the parent id.
    this.dataSource.parent_id = this.parent_id;

    this.dataSource.getData(false);
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      this.dataSource.order_by = this.sort.active;

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Asset Subtype.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create asset subtypes.');
      return;
    }
    // Deliberately not routed through onEdit(), which would check the edit permission and block a
    // create-only user.
    this.openDialog(new AssetTypeModel());
  }

  /**
   * Update an existing Asset Subtype.
   *
   * @param {AssetTypeModel} assetType The asset subtype to be edited.
   * @return {void}
   */
  onEdit(assetType: AssetTypeModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit asset subtypes.');
      return;
    }
    this.openDialog(assetType);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), which each guard their own
   * permission before calling this.
   *
   * @param {AssetTypeModel} assetType The asset subtype to be created or edited.
   * @return {void}
   */
  private openDialog(assetType: AssetTypeModel): void {
    this.dialog
      .open(AssetTypesEditComponent, {
        width: '900px',
        data: {
          asset_type_id: assetType.id,
          on_parent_list: this.on_parent_list,
          parent_id: this.parent_id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  /**
   * Show the Asset Subtypes view component dialog with the provided ID.
   *
   * @param {number} id - The unique identifier of the asset subtype.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset subtypes.');
      return;
    }
    this.utils.showComponentDialog(
      AssetTypesViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives selected Asset Subtypes.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive asset subtypes.');
      return;
    }
    this.utils.showModal(
      'Archive Asset Subtype(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset subtype(s)? If a Parent Asset Type is selected then all Subtypes will be archived as well.`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset Subtype(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset Subtype.
   *
   * @param {AssetTypeModel} assetType - The asset subtype to be archived.
   * @return {void}
   */
  onRemove(assetType: AssetTypeModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive asset subtypes.');
      return;
    }
    this.utils.showModal(
      `Archive Asset ${!assetType.parent ? 'Type' : 'Subtype'}`,
      `Are you sure you want to archive "${assetType.name}"?${!assetType.parent ? ' If there are any Subtypes then they will be archived as well.' : ''}`,
      () => {
        this.remove(assetType.id, `Asset ${!assetType.parent ? 'Type' : 'Subtype'} has been archived.`);
      }
    );
  }

  /**
   * Archive Asset Subtypes with the specified IDs.
   *
   * @param {any} ids - The asset subtype IDs to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/asset-types/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset Subtype.
   */
  onRestore(id: number) {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore asset subtypes.');
      return;
    }
    this.utils.showModal(
      'Restore Asset Subtype',
      'Are you sure you want to restore this asset subtype?',
      () => {
        this.restore(id, 'The Asset Subtype was restored.');
      }
    );
  }

  /**
   * Restore selected Asset Subtypes.
   */
  onRestoreSelected():void {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore asset subtypes.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Asset Subtypes',
      'Are you sure you want to restore the selected asset subtypes?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Asset Subtype(s) were restored.');
      }
    );
  }

  /**
   * Restore Asset Subtypes with the specified IDs.
   *
   * @param {any} ids - The asset subtype IDs to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/asset-types/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering Asset Subtypes.
   *
   * @return {void}
   */
  onFilter(): void {
    // Filtering only narrows the list, so it is meaningless without the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view asset subtypes.');
      return;
    }
    this.utils.showComponentDialog(AssetTypesFilterComponent, {
        filter_archived: this.dataSource.archived,
        filter_on_parent_list: this.on_parent_list
      }, {
        width: '500px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.archived = results.archived ? results.archived : this.dataSource.archived;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Asset Subtypes into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'): void {
    // Exporting reveals the records themselves, so it requires the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export asset subtypes.');
      return;
    }
    this.dataSource.makeDownloadRequest(`v2/asset-types/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      archived: this.dataSource.archived,
      parent_id: this.parent_id
    })
      .then((response) => {
        saveAs(response, `Asset Subtypes - ${this.parent_id} - ${moment().toString()}.${type}`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

}

export class AssetSubtypesDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = "false";
  parent_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/asset-types', resetOffset, {
      archived: this.archived,
      parent_id: this.parent_id
    });
  }
}
